import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { Loader2 } from "lucide-react";

export default function PostponeTaskDialog({ task, onClose, onDone }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [newDue, setNewDue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) setNewDue(task.due?.slice(0, 10) || "");
  }, [task]);

  if (!task) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/ops/tasks/${task.task_id}`, {
        status: "postponed",
        due: newDue || null,
      });
      toast.success(t("ops.postpone.saved"));
      onClose?.();
      onDone?.();
    } catch { toast.error(t("ops.form.fail")); }
    finally { setSaving(false); }
  };

  const removeFromPlan = async () => {
    if (!window.confirm(t("ops.confirm_delete"))) return;
    try {
      await api.delete(`/ops/tasks/${task.task_id}`);
      toast.success(t("ops.deleted"));
      onClose?.();
      onDone?.();
    } catch { toast.error(t("ops.form.fail")); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="task-postpone-dialog"
        className="max-w-md bg-stone-950 border-stone-800">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {t("ops.postpone.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <p className="text-stone-300 text-sm">{t("ops.postpone.q")}</p>
          <input
            data-testid="postpone-date"
            type="date"
            value={newDue}
            onChange={(e) => setNewDue(e.target.value)}
            className="script-input w-full"
          />

          {task.postpone_count >= 2 && (
            <div className="text-xs italic text-amber-200 bg-amber-500/5 border border-amber-500/30 rounded-sm p-3">
              {t("ops.postpone.repeated_note")}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-4 flex-wrap">
          <button
            onClick={removeFromPlan}
            data-testid="postpone-remove"
            className="ghost-btn text-xs hover:!text-rose-400 hover:!border-rose-500/50"
          >
            {t("ops.postpone.remove")}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="ghost-btn">{t("action.cancel")}</button>
            <button
              onClick={submit}
              data-testid="postpone-save"
              disabled={saving}
              className="pill-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("ops.postpone.save")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
